import { AxiosResponse } from "axios";
import { ApiTenantSettings } from ".";
import { HttpMethods } from "../httpMethods";
import { Controllers, TenantSettingsRoutes } from "src/utils/constants";
import { BaseService } from "../baseService";
import {
	Cc4AllResourceDefaults,
	Cc4AllResourcesSnapshot,
	TenantSettingsModel,
} from "src/types/tenantSettings";

export class TenantSettingsService
	extends BaseService
	implements ApiTenantSettings {
	getDefaultFont(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.DefaultFont
		);
	}

	checkIfLicenseStatusIsEnabled(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.CheckLicenseStatusEnabled
		);
	}

	getTenantSettings(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			""
		);
	}
	// Lightweight id+name list of the tenant's Salesforce lookup configurations. Unlike
	// getTenantSettings (WebAdmin-only), this endpoint is authorized for queue editors
	// (Supervisor role), so the queue integrations picker populates without a 403.
	getCrmLookupConfigurations(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.LookupConfigurationSummaries
		);
	}
	saveTenantSettings(
		tenantSettingModel: TenantSettingsModel
	): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Post,
			Controllers.TenantSettings,
			"",
			null,
			tenantSettingModel
		);
	}
	getTimezones(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.Timezones
		);
	}
	checkPresenceAccess(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.CheckPresenceAccess
		);
	}
	checkGroupIsValid(groupId: string): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.CheckGroupIsValid,
			`?groupId=${groupId}`
		);
	}
	checkOpenAISettings(
		openAIEndpoint: string,
		openAIKey: string,
		modelName?: string,
		assistantId?: string
	): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Post,
			Controllers.TenantSettings,
			TenantSettingsRoutes.CheckOpenAISettings,
			"",
			{ endpoint: openAIEndpoint, key: openAIKey, modelName, assistantId }
		);
	}

	checkMicrosoftFoundrySettings(
		microsoftFoundryEndpoint: string,
		microsoftFoundryTenantId: string,
		modelName?: string,
		agentId?: string
	): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Post,
			Controllers.TenantSettings,
			`${TenantSettingsRoutes.CheckMicrosoftFoundrySettings}`,
			"",
			{ endpoint: microsoftFoundryEndpoint, tenantId: microsoftFoundryTenantId, agentId, modelName }
		);
	}

	getCc4AllDefaults(): Promise<AxiosResponse<Cc4AllResourceDefaults>> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.Cc4AllDefaults
		);
	}

	getCc4AllSnapshot(): Promise<AxiosResponse<Cc4AllResourcesSnapshot>> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.Cc4AllSnapshot
		);
	}

	async resyncTenant(tenantId: string, azureTenantId?: string): Promise<AxiosResponse> {
		const queryString = azureTenantId ? `?tenantId=${tenantId}&azureTenantId=${azureTenantId}` : `?tenantId=${tenantId}`;

		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.Resync,
			queryString
		);
	}

	getPermissions(): Promise<AxiosResponse> {
		return this.invokeServiceCall(
			HttpMethods.Get,
			Controllers.TenantSettings,
			TenantSettingsRoutes.Permissions
		);
	}
}
